import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, Target, Shield, Users, Clock, Globe,
  ArrowRight, Zap, Activity, Wind, Sun, Battery,
  TrendingUp, Settings, Database, Eye, Brain, Camera,
  Gauge, Package, LineChart, Cpu, ChevronRight,
} from 'lucide-react';
import Layout from '../../components/layout/Layout';
import Reveal from '../../components/enterprise/Reveal';
import SectionHead from '../../components/enterprise/SectionHead';
import '../../styles/enterprise.css';

/* The four technology domains the platform covers. */
const technologies = [
  { name: 'Solar Energy', icon: Sun },
  { name: 'Wind Energy', icon: Wind },
  { name: 'Battery Storage', icon: Battery },
  { name: 'Grid Integration', icon: Activity },
];

/* Headline capabilities, kept from the previous page with their feature lists. */
const featured = [
  {
    icon: BarChart3,
    title: 'Asset Performance Monitoring',
    description:
      'Monitor and assess the health of renewable asset portfolios in one view, highlight which assets need technical attention.',
    features: ['Real-time monitoring', 'Performance analytics', 'Health assessment', 'Technical alerts'],
    link: '/service/asset-performance',
  },
  {
    icon: Target,
    title: 'AI-Driven Analytics',
    description:
      'Apply pre-trained models to identify root causes of energy losses and bridge the gap between target and actual generation.',
    features: ['Machine learning models', 'Root cause analysis', 'Performance optimization', 'Predictive insights'],
    link: '/service/ai-analytics',
  },
  {
    icon: Settings,
    title: 'Automated Workflows',
    description:
      'Automated workflows that identify root causes and recommend corrective actions in near real-time to maximize efficiency.',
    features: ['Process automation', 'Corrective actions', 'Real-time alerts', 'Efficiency optimization'],
    link: '/service/solar-advanced-analytics',
  },
  {
    icon: Database,
    title: 'Data Collection & Analysis',
    description:
      'Use proven methods to collect and clean data from renewable assets, providing actionable insights for operational excellence.',
    features: ['Data collection', 'Data cleaning', 'Actionable insights', 'Operational excellence'],
    link: '/service/clean-energy-data-analysis',
  },
  {
    icon: Globe,
    title: 'Portfolio Management',
    description:
      'Comprehensive portfolio-wide view with streamlined reporting to promote seamless alignment with regulatory requirements.',
    features: ['Portfolio overview', 'Regulatory compliance', 'Streamlined reporting', 'Performance tracking'],
    link: '/service/portfolio-management',
  },
];

/* Full directory — every service detail page, grouped by discipline. */
const directory = [
  {
    label: 'Monitoring & diagnostics',
    items: [
      {
        icon: BarChart3,
        title: 'Asset Performance Monitoring',
        description: 'Assess the health of renewable asset portfolios in one view.',
        link: '/service/asset-performance-monitoring',
      },
      {
        icon: Activity,
        title: 'Asset Monitoring',
        description: 'Continuous 24/7 monitoring with instant visualisation and smart alerts.',
        link: '/service/asset-monitoring',
      },
      {
        icon: Eye,
        title: 'Asset Performance',
        description: 'Real-time surveillance, analytics and technical alerts across assets.',
        link: '/service/asset-performance',
      },
      {
        icon: Cpu,
        title: 'Renewable Technology Monitoring',
        description: 'One platform across solar, wind, storage and hybrid generation.',
        link: '/service/renewable-technology-monitoring',
      },
      {
        icon: Wind,
        title: 'Wind Turbine Monitoring',
        description: 'Monitor, diagnose and extend turbine life with root-cause diagnostics.',
        link: '/service/wind-turbine-monitoring',
      },
    ],
  },
  {
    label: 'Analytics & AI',
    items: [
      {
        icon: Target,
        title: 'AI Analytics',
        description: 'Pre-trained models that close the gap between target and actual generation.',
        link: '/service/ai-analytics',
      },
      {
        icon: LineChart,
        title: 'Performance Analytics',
        description: 'Turn raw operational data into production, loss and financial insight.',
        link: '/service/performance-analytics',
      },
      {
        icon: Sun,
        title: 'Solar Advanced Analytics',
        description: 'Benchmark sites, detect anomalies and automate corrective action.',
        link: '/service/solar-advanced-analytics',
      },
      {
        icon: Brain,
        title: 'Wind Turbines AI',
        description: 'Machine learning applied to wind turbine performance and anomalies.',
        link: '/service/wind-turbines-ai',
      },
      {
        icon: Zap,
        title: 'Energy Innovation AI',
        description: 'AI-driven analytics that surface new optimisation opportunities.',
        link: '/service/energy-innovation-ai',
      },
      {
        icon: Camera,
        title: 'Image & Video Analytics',
        description: 'Thermal and visual inspection with automated defect detection.',
        link: '/service/image-video-analytics',
      },
    ],
  },
  {
    label: 'Data & platform',
    items: [
      {
        icon: Database,
        title: 'Data Ingestion',
        description: 'OPC-UA, MODBUS, MQTT, Kafka and REST into one normalised feed.',
        link: '/service/data-ingestion',
      },
      {
        icon: Shield,
        title: 'Clean Energy Data Analysis',
        description: 'Proven methods to collect, clean and validate renewable asset data.',
        link: '/service/clean-energy-data-analysis',
      },
      {
        icon: Globe,
        title: 'Portfolio Management',
        description: 'Portfolio-wide oversight with streamlined regulatory reporting.',
        link: '/service/portfolio-management',
      },
    ],
  },
  {
    label: 'Optimisation & operations',
    items: [
      {
        icon: Clock,
        title: 'RTC Power Optimization',
        description: 'Round-the-clock optimisation of generation, storage and grid delivery.',
        link: '/service/rtc-power-optimization',
      },
      {
        icon: TrendingUp,
        title: 'Energy Trading Optimization',
        description: 'Market analysis, price forecasting and trading strategy optimisation.',
        link: '/service/energy-trading-optimization',
      },
      {
        icon: Sun,
        title: 'Solar Energy Farm',
        description: 'Monitoring and optimisation for large-scale solar installations.',
        link: '/service/solar-energy-farm',
      },
      {
        icon: Wind,
        title: 'Wind Energy Repowering',
        description: 'Repower stressed assets to meet PPA limits and extend farm life.',
        link: '/service/wind-repowering',
      },
      {
        icon: Gauge,
        title: 'Offline Measurement',
        description: 'Precision field testing and calibration with certified equipment.',
        link: '/service/offline-measurement',
      },
      {
        icon: Package,
        title: 'Spares & Vendors Management',
        description: 'Inventory optimisation, vendor scorecards and procurement workflows.',
        link: '/service/spares-vendors-management',
      },
    ],
  },
];

const benefits = [
  {
    icon: TrendingUp,
    title: 'Increased efficiency',
    description: 'Optimize energy generation and reduce operational costs through advanced analytics.',
  },
  {
    icon: Shield,
    title: 'Enhanced reliability',
    description: 'Minimize downtime and ensure consistent performance with predictive maintenance.',
  },
  {
    icon: Users,
    title: 'Better collaboration',
    description: 'Streamline team coordination and improve decision-making with comprehensive reporting.',
  },
  {
    icon: Clock,
    title: '24/7 monitoring',
    description: 'Round-the-clock surveillance and automated alerts for immediate response.',
  },
];

const process = [
  {
    title: 'Assessment',
    description: 'Comprehensive analysis of your current operations and performance metrics.',
  },
  {
    title: 'Implementation',
    description: 'Seamless integration of our monitoring and analytics platform.',
  },
  {
    title: 'Optimization',
    description: 'Continuous improvement and performance optimization based on data insights.',
  },
  {
    title: 'Support',
    description: 'Ongoing support and maintenance to ensure optimal performance.',
  },
];

const totalServices = directory.reduce((sum, group) => sum + group.items.length, 0);

const ServicesPage = () => {
  useEffect(() => {
    document.body.classList.add('ibm-plex-page');
    return () => document.body.classList.remove('ibm-plex-page');
  }, []);

  return (
    <Layout header={6} footer={6}>
      <main className="ax">
        {/* --- Hero --------------------------------------------------------- */}
        <section className="ax-hero">
          <div className="ax-hero__bg">
            <img
              src="/assets/img/general/steptodown.com430799.jpg"
              alt=""
            />
          </div>
          <div className="ax-hero__scrim" />
          <div className="ax-hero__inner">
            <div className="ax-container">
              <div className="ax-hero__copy">
                <Reveal className="ax-crumbs">
                  <Link to="/">Home</Link>
                  <ChevronRight />
                  <span>Services</span>
                </Reveal>
                <Reveal delay={60} style={{ display: 'block', marginTop: 28 }}>
                  <span className="ax-eyebrow">Services</span>
                </Reveal>
                <Reveal delay={120}>
                  <h1 className="ax-display">
                    Comprehensive renewable <em>energy solutions</em>
                  </h1>
                </Reveal>
                <Reveal delay={180}>
                  <p className="ax-hero__lede">
                    From solar to wind, battery storage to grid optimization — we provide
                    end-to-end solutions for modern renewable energy operations with advanced
                    analytics and AI-driven insights.
                  </p>
                </Reveal>
                <Reveal delay={240} className="ax-hero__actions">
                  <a href="#directory" className="ax-btn ax-btn--invert">
                    <span>Browse all {totalServices} services</span>
                    <ArrowRight className="ax-btn__arrow" size={17} />
                  </a>
                  <Link to="/contact" className="ax-btn ax-btn--ghost-invert">
                    <span>Talk to our team</span>
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* --- Technologies covered ------------------------------------------ */}
        <div className="ax-container">
          <div className="ax-techstrip">
            {technologies.map((tech, i) => {
              const Icon = tech.icon;
              return (
                <Reveal key={tech.name} className="ax-techstrip__item" delay={i * 70}>
                  <Icon />
                  <span>{tech.name}</span>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* --- Featured capabilities ----------------------------------------- */}
        <section className="ax-section">
          <div className="ax-container">
            <SectionHead
              eyebrow="Core capabilities"
              title="Monitor, analyze, and manage"
              lede="Your renewable energy assets, with a comprehensive suite of advanced analytics and monitoring solutions."
            />
            <div className="ax-grid ax-grid--3 ax-grid--hairline" style={{ marginTop: 56 }}>
              {featured.map((service, i) => {
                const Icon = service.icon;
                return (
                  <Reveal
                    as={Link}
                    to={service.link}
                    key={service.title}
                    className="ax-card ax-card--link"
                    delay={(i % 3) * 70}
                  >
                    <span className="ax-card__icon">
                      <Icon />
                    </span>
                    <h3 className="ax-h3">{service.title}</h3>
                    <p className="ax-body" style={{ marginBottom: 20 }}>
                      {service.description}
                    </p>
                    <ul style={{ display: 'grid', gap: 8 }}>
                      {service.features.map((feature) => (
                        <li
                          key={feature}
                          className="ax-small"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '5px 1fr',
                            gap: 12,
                            alignItems: 'center',
                          }}
                        >
                          <span
                            style={{
                              width: 5,
                              height: 5,
                              background: 'var(--ax-blue-300)',
                              display: 'block',
                            }}
                          />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <span className="ax-card__go">
                      Explore service
                      <ArrowRight size={15} />
                    </span>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* --- Full directory ------------------------------------------------- */}
        <section className="ax-section ax-section--alt" id="directory">
          <div className="ax-container">
            <SectionHead
              eyebrow="All services"
              title="The complete service catalogue"
              lede={`${totalServices} services across monitoring, analytics, data and operations — each with its own detail page.`}
            />

            <div style={{ marginTop: 56 }}>
              {directory.map((group) => (
                <div className="ax-dir__group" key={group.label}>
                  <Reveal className="ax-dir__label">
                    <h3 className="ax-h4">{group.label}</h3>
                    <span className="ax-dir__count">
                      {String(group.items.length).padStart(2, '0')}
                    </span>
                  </Reveal>

                  <div className="ax-grid ax-grid--3 ax-grid--hairline">
                    {group.items.map((item, i) => {
                      const Icon = item.icon;
                      return (
                        <Reveal
                          as={Link}
                          to={item.link}
                          key={item.title}
                          className="ax-card ax-card--link"
                          delay={(i % 3) * 60}
                        >
                          <span className="ax-card__icon">
                            <Icon />
                          </span>
                          <h4 className="ax-h3">{item.title}</h4>
                          <p className="ax-body">{item.description}</p>
                          <span className="ax-card__go">
                            View
                            <ArrowRight size={15} />
                          </span>
                        </Reveal>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- How we work ---------------------------------------------------- */}
        <section className="ax-section">
          <div className="ax-container">
            <SectionHead
              eyebrow="How we work"
              title="A streamlined path to production"
              lede="A streamlined approach to implementing and optimizing your renewable energy analytics solution."
            />
            <div
              className="ax-process"
              style={{ marginTop: 56, '--ax-process-count': process.length }}
            >
              {process.map((step, i) => (
                <Reveal key={step.title} className="ax-process__step" delay={i * 80}>
                  <span className="ax-process__num">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="ax-h4">{step.title}</h3>
                  <p className="ax-body">{step.description}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* --- Benefits -------------------------------------------------------- */}
        <section className="ax-section ax-section--ink">
          <div className="ax-container">
            <SectionHead
              eyebrow="Benefits"
              title="Benefits of our solutions"
              lede="Discover how our advanced analytics and monitoring solutions can transform your renewable energy operations."
            />
            <div className="ax-grid ax-grid--4 ax-grid--gap" style={{ marginTop: 56 }}>
              {benefits.map((benefit, i) => {
                const Icon = benefit.icon;
                return (
                  <Reveal
                    key={benefit.title}
                    delay={i * 70}
                    style={{ borderTop: '2px solid rgba(255,255,255,0.22)', paddingTop: 24 }}
                  >
                    <Icon size={20} style={{ color: 'var(--ax-blue-300)', marginBottom: 16 }} />
                    <h3 className="ax-h4" style={{ color: '#fff', marginBottom: 10 }}>
                      {benefit.title}
                    </h3>
                    <p className="ax-body" style={{ color: 'rgba(255,255,255,0.74)' }}>
                      {benefit.description}
                    </p>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* --- CTA -------------------------------------------------------------- */}
        <section className="ax-section ax-section--tight ax-rule">
          <div className="ax-container">
            <div className="ax-split" style={{ alignItems: 'center' }}>
              <Reveal>
                <span className="ax-eyebrow">Next step</span>
                <h2 className="ax-h2" style={{ marginTop: 16 }}>
                  Ready to optimize your renewable energy operations?
                </h2>
                <p className="ax-lede" style={{ marginTop: 16 }}>
                  Get started with our comprehensive analytics and monitoring solutions today.
                </p>
              </Reveal>
              <Reveal delay={80} style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                <Link to="/contact" className="ax-btn ax-btn--primary">
                  <span>Get started today</span>
                  <ArrowRight className="ax-btn__arrow" size={17} />
                </Link>
                <Link to="/contact" className="ax-btn ax-btn--secondary">
                  <span>Schedule demo</span>
                </Link>
              </Reveal>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default ServicesPage;
